import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Chrome, Apple, Phone, Mail, ArrowRight, ArrowLeft, Camera, Check, Sparkles, X, Lock, Loader2, ImagePlus, MailWarning, Users } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/brand/Logo";
import { AuraRing } from "@/components/brand/AuraRing";
import { VerifiedBadge } from "@/components/brand/VerifiedBadge";
import { ProfessionSelect } from "@/components/app/ProfessionSelect";
import { useAura } from "@/context/AuraContext";
import { useAuth } from "@/context/AuthContext";
import { api, mediaUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

const STEPS = ["join", "username", "profile", "follow", "post", "done"];

export default function Onboarding() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setProfile, setOnboarded } = useAura();
  const { user, register, loginWithGoogle, checkAuth, resendVerification } = useAuth();
  const [avail, setAvail] = useState(null);
  const [checking, setChecking] = useState(false);
  const [uErr, setUErr] = useState("");
  const [step, setStep] = useState(0);
  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [category, setCategory] = useState("");
  const [avatar, setAvatar] = useState("");
  const [banner, setBanner] = useState("");
  const [uploading, setUploading] = useState("");
  const [suggested, setSuggested] = useState(null);
  const [followed, setFollowed] = useState({});
  const [postText, setPostText] = useState("");
  const [postMedia, setPostMedia] = useState(null);
  const [posting, setPosting] = useState(false);
  const [referrer, setReferrer] = useState(null);
  const [refCode, setRefCode] = useState(null); // invited-by handle, held server-side in an httpOnly cookie

  const [emailMode, setEmailMode] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState("");

  const total = STEPS.length;
  const next = () => setStep((s) => Math.min(s + 1, total - 1));
  const back = () => (step === 0 ? navigate("/") : setStep((s) => s - 1));

  // ?ref=username → server stores it in an httpOnly cookie for 30 days; otherwise read any pending one.
  useEffect(() => {
    const ref = (params.get("ref") || "").trim().replace(/^@/, "").toLowerCase();
    const req = ref ? api.trackReferral(ref) : api.pendingReferral();
    req.then((r) => setRefCode(r.code || null)).catch((e) => console.warn("referral lookup failed", e));
  }, [params]);
  const seeded = useRef(false);
  useEffect(() => {
    if (!user) return;
    if (user.onboarding_complete !== false) { navigate("/home", { replace: true }); return; }
    if (seeded.current) return;
    seeded.current = true;
    setName(user.name || ""); setAvatar(user.picture || ""); setUsername(user.username || "");
    setStep((s) => (s === 0 ? 1 : s));
  }, [user, navigate]);

  // Attach the cookie-held referral once the account exists (one attempt per mount).
  const attached = useRef(false);
  useEffect(() => {
    if (!user || step < 1 || user.referred_by || attached.current || !refCode) return;
    attached.current = true;
    api.attachReferral().then((r) => {
      if (r.ok) { setReferrer(r.referrer); checkAuth(); }
    }).catch((e) => { attached.current = false; console.warn("referral attach failed", e); });
  }, [user, step, checkAuth, refCode]);

  const doRegister = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setAuthError("Please enter a display name."); return; }
    setAuthError(""); setAuthBusy(true);
    try { await register(email.trim(), password, name.trim()); next(); }
    catch (err) { setAuthError(err.message); }
    finally { setAuthBusy(false); }
  };

  const upload = async (file, kind) => {
    if (!file) return; setUploading(kind);
    try { const r = await api.uploadMedia(file); kind === "avatar" ? setAvatar(r.url) : kind === "banner" ? setBanner(r.url) : setPostMedia(r); }
    catch (e) { toast(e.message); } finally { setUploading(""); }
  };

  useEffect(() => {
    if (step !== 3) return;
    api.onboardingSuggestions().then((list) => {
      setSuggested(list);
      const init = {}; list.forEach((m) => { init[m.username] = !!(m.is_following || m.state === "following" || m.state === "friends"); });
      setFollowed(init);
    }).catch(() => setSuggested([]));
  }, [step]);

  const toggleFollow = async (m) => {
    const now = !followed[m.username];
    setFollowed((f) => ({ ...f, [m.username]: now }));
    try { now ? await api.follow(m.username) : await api.unfollow(m.username); }
    catch (e) { setFollowed((f) => ({ ...f, [m.username]: !now })); toast(e.message); }
  };
  const followAll = async () => {
    const names = (suggested || []).filter((m) => !followed[m.username]).map((m) => m.username);
    if (!names.length) return;
    setFollowed((f) => { const n = { ...f }; names.forEach((u) => { n[u] = true; }); return n; });
    try { await api.followAll(names); toast(`Following ${names.length} creator${names.length > 1 ? "s" : ""}`); } catch (e) { toast(e.message); }
  };

  const saveProfile = async () => {
    try { await api.updateProfile({ name: name || username, bio: bio.slice(0, 160), picture: avatar, banner, category }); await checkAuth(); }
    catch (e) { toast(e.message); }
  };

  const publish = async () => {
    if (!postText.trim() && !postMedia) return;
    setPosting(true);
    try {
      await api.createPost({ text: postText.trim(), media: postMedia ? [{ url: postMedia.url, type: postMedia.type }] : [] });
      toast("Your first post is live");
      next();
    } catch (e) { toast(e.message); } finally { setPosting(false); }
  };

  const finish = async () => {
    try { await api.onboardingComplete(); await checkAuth(); } catch (e) { console.error("onboarding finish failed", e); }
    setProfile({ name: name || "You", username: username || "yournewaura", bio, avatar, interests: [] });
    setOnboarded(true);
    navigate("/home");
  };

  useEffect(() => {
    setUErr("");
    if (username.trim().length < 2) { setAvail(null); return; }
    let active = true; setChecking(true);
    const t = setTimeout(async () => {
      try { const r = await api.usernameAvailable(username.trim()); if (active) { setAvail(r.available); if (!r.available) setUErr(r.reason || "Taken"); } }
      catch { if (active) setAvail(null); } finally { if (active) setChecking(false); }
    }, 350);
    return () => { active = false; clearTimeout(t); };
  }, [username]);

  const handleContinue = async () => {
    if (step === 1) { setUErr(""); try { await api.setUsername(username.trim()); await checkAuth(); } catch (e) { setUErr(e.message); return; } }
    if (step === 2) await saveProfile();
    next();
  };

  const canNext = step === 1 ? (username.trim().length >= 2 && avail === true) : true;
  const verified = !!user?.email_verified || !!user?.allow_post_unverified;

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-am-bg px-4 py-8">
      <div className="pointer-events-none absolute left-1/2 top-0 h-96 w-96 -translate-x-1/2 -translate-y-1/3 rounded-full aura-radial opacity-15 blur-[110px]" />
      {step === 0 && <button onClick={() => navigate("/")} className="absolute right-5 top-5 grid h-9 w-9 place-items-center rounded-full text-am-text2 hover:bg-white/5"><X className="h-5 w-5" /></button>}

      <div className="relative w-full max-w-md">
        <div className="mb-8 flex items-center gap-2">
          {STEPS.map((k, i) => (
            <div key={k} className="h-1 flex-1 overflow-hidden rounded-full bg-am-surface3">
              <div className={cn("h-full rounded-full transition-all duration-500", i <= step ? "aura-gradient" : "")} style={{ width: i <= step ? "100%" : "0%" }} />
            </div>
          ))}
        </div>

        <div key={step} className="animate-fade-up" data-testid={`onboarding-step-${STEPS[step]}`}>
          {step === 0 && (
            <div className="text-center">
              <Logo size={72} className="mx-auto" />
              <h1 className="mt-6 font-display text-3xl font-extrabold text-white">Join AuraMax</h1>
              <p className="mt-2 text-sm text-am-text2">Create your account and start building your aura.</p>
              {refCode && <p className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-am-border bg-am-surface2 px-3 py-1 text-xs text-am-text2" data-testid="join-ref-chip"><Users className="h-3.5 w-3.5 aura-text" /> Invited by @{refCode}</p>}
              {!emailMode ? (
                <div className="mt-8 flex flex-col gap-3">
                  <button onClick={loginWithGoogle} data-testid="join-google" className="flex items-center justify-center gap-3 rounded-full border border-am-border-strong bg-am-surface2 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/5"><Chrome className="h-5 w-5" /> Continue with Google</button>
                  <button onClick={() => toast("Apple sign-in is prototype-only", { description: "Use Google or Email to create a real account." })} data-testid="join-apple" className="flex items-center justify-center gap-3 rounded-full border border-am-border bg-am-surface2 py-3.5 text-sm font-semibold text-am-text2 transition-colors hover:text-white"><Apple className="h-5 w-5" /> Continue with Apple</button>
                  <button onClick={() => toast("Phone sign-in is prototype-only", { description: "Use Google or Email to create a real account." })} data-testid="join-number" className="flex items-center justify-center gap-3 rounded-full border border-am-border bg-am-surface2 py-3.5 text-sm font-semibold text-am-text2 transition-colors hover:text-white"><Phone className="h-5 w-5" /> Use Phone Number</button>
                  <button onClick={() => setEmailMode(true)} data-testid="join-email" className="flex items-center justify-center gap-3 rounded-full border border-am-border-strong bg-am-surface2 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/5"><Mail className="h-5 w-5" /> Use Email</button>
                </div>
              ) : (
                <form onSubmit={doRegister} className="mt-8 flex flex-col gap-3 text-left">
                  <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="join-name" placeholder="Display name (required)" className="w-full rounded-xl border border-am-border bg-am-surface2 px-4 py-3 text-sm text-white placeholder:text-am-text3 outline-none focus:border-am-border-strong" />
                  <div className="flex items-center rounded-xl border border-am-border bg-am-surface2 px-3 focus-within:border-am-border-strong">
                    <Mail className="h-4 w-4 text-am-text3" />
                    <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid="join-email-input" placeholder="Email" className="w-full bg-transparent px-3 py-3 text-sm text-white placeholder:text-am-text3 outline-none" />
                  </div>
                  <div className="flex items-center rounded-xl border border-am-border bg-am-surface2 px-3 focus-within:border-am-border-strong">
                    <Lock className="h-4 w-4 text-am-text3" />
                    <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required minLength={6} data-testid="join-password-input" placeholder="Password (min 6 chars)" className="w-full bg-transparent px-3 py-3 text-sm text-white placeholder:text-am-text3 outline-none" />
                  </div>
                  {authError && <p data-testid="join-error" className="rounded-lg bg-aura-magenta/10 px-3 py-2 text-xs text-aura-magenta">{authError}</p>}
                  <button type="submit" disabled={authBusy} data-testid="join-create" className="flex items-center justify-center gap-2 rounded-full aura-gradient py-3.5 text-sm font-bold text-black disabled:opacity-60">{authBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create account <ArrowRight className="h-4 w-4" /></>}</button>
                  <button type="button" onClick={() => { setEmailMode(false); setAuthError(""); }} className="text-center text-xs text-am-text3 hover:text-white">← other options</button>
                </form>
              )}
              <p className="mt-6 text-xs text-am-text3">Already have an account? <button onClick={() => navigate("/login")} className="font-semibold aura-text" data-testid="join-login-link">Log in</button></p>
            </div>
          )}

          {step === 1 && (
            <div>
              <h1 className="font-display text-3xl font-extrabold text-white">Pick a username</h1>
              <p className="mt-2 text-sm text-am-text2">This will be your @handle and your referral code. You can change it later.</p>
              <div className="mt-8 flex items-center rounded-2xl border border-am-border bg-am-surface2 px-4 focus-within:border-am-border-strong">
                <span className="text-lg text-am-text3">@</span>
                <input autoFocus value={username} onChange={(e) => setUsername(e.target.value.replace(/[^a-z0-9_]/gi, "").toLowerCase())} data-testid="onboarding-username" placeholder="yourname" className="w-full bg-transparent py-4 pl-1 text-lg text-white placeholder:text-am-text3 outline-none" />
                {checking ? <Loader2 className="h-5 w-5 animate-spin text-am-text3" /> : avail === true && username.length >= 2 ? <Check className="h-5 w-5 text-aura-mint" /> : avail === false ? <X className="h-5 w-5 text-aura-magenta" /> : null}
              </div>
              {uErr && <p data-testid="onboarding-username-error" className="mt-3 text-xs text-aura-magenta">{uErr}</p>}
              {avail === true && username.length >= 2 && !uErr && <p className="mt-3 text-xs text-aura-mint">@{username} is available</p>}
            </div>
          )}

          {step === 2 && (
            <div>
              <h1 className="font-display text-3xl font-extrabold text-white">Set up your profile</h1>
              <p className="mt-2 text-sm text-am-text2">A photo, a banner and a line about you. Everything here is optional.</p>
              <div className="relative mt-6 h-28 overflow-hidden rounded-2xl bg-am-surface2">
                {banner ? <img src={mediaUrl(banner)} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full aura-gradient opacity-25" />}
                <label className="absolute right-2 top-2 flex cursor-pointer items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-xs font-semibold text-white">
                  {uploading === "banner" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} {banner ? "Change banner" : "Add banner"}
                  <input type="file" hidden accept="image/*" data-testid="onboarding-upload-banner" onChange={(e) => upload(e.target.files[0], "banner")} />
                </label>
              </div>
              <div className="-mt-10 ml-4 flex items-end gap-3">
                <div className="relative">
                  <AuraRing src={mediaUrl(avatar) || undefined} size={84} ring={3} fallback={name || username} />
                  <label className="absolute -bottom-1 -right-1 grid h-8 w-8 cursor-pointer place-items-center rounded-full border-2 border-am-bg aura-gradient text-black">
                    {uploading === "avatar" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                    <input type="file" hidden accept="image/*" data-testid="onboarding-upload-avatar" onChange={(e) => upload(e.target.files[0], "avatar")} />
                  </label>
                </div>
                <span className="pb-2 text-xs text-am-text3">Profile photo</span>
              </div>
              <div className="mt-5 space-y-3">
                <input value={name} onChange={(e) => setName(e.target.value)} data-testid="onboarding-name" placeholder="Display name" className="w-full rounded-xl border border-am-border bg-am-surface2 px-3 py-2.5 text-sm text-white placeholder:text-am-text3 outline-none focus:border-am-border-strong" />
                <ProfessionSelect value={category} onChange={setCategory} testid="onboarding-profession" />
                <div>
                  <textarea value={bio} onChange={(e) => setBio(e.target.value.slice(0, 160))} data-testid="onboarding-bio" placeholder="Bio — say who you are" rows={2} className="w-full resize-none rounded-xl border border-am-border bg-am-surface2 px-3 py-2.5 text-sm text-white placeholder:text-am-text3 outline-none focus:border-am-border-strong" />
                  <div className="mt-1 text-right text-[11px] text-am-text3">{bio.length}/160</div>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h1 className="font-display text-3xl font-extrabold text-white">Follow a few creators</h1>
              <p className="mt-2 text-sm text-am-text2">Your For You feed starts here. Follow who you like — you can always change it.</p>
              {suggested && suggested.length > 0 && (
                <button onClick={followAll} data-testid="onboarding-follow-all" className="mt-4 w-full rounded-full border border-am-border-strong py-2.5 text-sm font-semibold text-white hover:bg-white/5">Follow all</button>
              )}
              <div className="mt-4 max-h-[46vh] space-y-2.5 overflow-y-auto pr-1">
                {suggested === null ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-am-text3" /></div>
                  : suggested.length === 0 ? <p className="rounded-2xl border border-am-border bg-am-surface2 px-4 py-6 text-center text-sm text-am-text2">Creators will appear here soon.</p>
                  : suggested.map((m) => (
                    <div key={m.username} data-testid={`suggest-${m.username}`} className={cn("flex items-center gap-3 rounded-2xl border bg-am-surface2 p-3", m.is_referrer ? "border-aura-mint/40" : "border-am-border")}>
                      <AuraRing src={mediaUrl(m.picture) || undefined} size={46} ring={2} fallback={m.name} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1 text-sm font-semibold text-white"><span className="truncate">{m.name}</span>{m.is_verified && <VerifiedBadge size={13} />}{m.is_referrer && <span className="ml-1 rounded-full bg-aura-mint/15 px-1.5 text-[10px] font-bold text-aura-mint">Invited you</span>}</div>
                        <div className="truncate text-xs text-am-text2">@{m.username}{m.category ? ` · ${m.category}` : ""}</div>
                      </div>
                      <button onClick={() => toggleFollow(m)} data-testid={`suggest-follow-${m.username}`} className={cn("shrink-0 rounded-full px-4 py-1.5 text-xs font-bold", followed[m.username] ? "border border-am-border-strong text-white" : "aura-gradient text-black")}>{followed[m.username] ? "Following" : "Follow"}</button>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h1 className="font-display text-3xl font-extrabold text-white">Your first post</h1>
              <p className="mt-2 text-sm text-am-text2">Say hi, share something you made — anything. Your streak starts with your first post.</p>
              {!verified ? (
                <div className="mt-6 rounded-2xl border border-aura-gold/30 bg-aura-gold/[0.06] p-4" data-testid="onboarding-verify-prompt">
                  <div className="flex items-center gap-2 text-sm font-semibold text-white"><MailWarning className="h-4 w-4 text-aura-gold" /> Confirm your email to post</div>
                  <p className="mt-1 text-xs text-am-text2">We sent a link to <span className="text-white">{user?.email}</span>. Confirm it, then come back — or skip for now and post later.</p>
                  <button onClick={async () => { try { await resendVerification(); toast("Verification email sent"); } catch (e) { toast(e.message); } }} data-testid="onboarding-resend" className="mt-3 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/20">Resend email</button>
                </div>
              ) : (
                <div className="mt-6 rounded-2xl border border-am-border bg-am-surface2 p-3">
                  <div className="flex gap-3">
                    <AuraRing src={mediaUrl(avatar) || undefined} size={40} ring={2} fallback={name || username} />
                    <textarea value={postText} onChange={(e) => setPostText(e.target.value)} data-testid="onboarding-post-text" placeholder="What's your aura today?" rows={3} className="w-full resize-none bg-transparent text-sm text-white placeholder:text-am-text3 outline-none" />
                  </div>
                  {postMedia && (
                    <div className="relative mt-2 overflow-hidden rounded-xl border border-am-border bg-black">
                      {postMedia.type === "video" ? <video src={mediaUrl(postMedia.url)} muted className="max-h-56 w-full object-contain" /> : <img src={mediaUrl(postMedia.url)} alt="" className="max-h-56 w-full object-contain" />}
                      <button onClick={() => setPostMedia(null)} className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-black/60 text-white"><X className="h-4 w-4" /></button>
                    </div>
                  )}
                  <div className="mt-2 flex items-center justify-between">
                    <label className="flex cursor-pointer items-center gap-1.5 rounded-full border border-am-border px-3 py-1.5 text-xs font-semibold text-am-text2 hover:text-white">
                      {uploading === "post" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} Add photo or video
                      <input type="file" hidden accept="image/*,video/*" data-testid="onboarding-post-media" onChange={(e) => upload(e.target.files[0], "post")} />
                    </label>
                    <button onClick={publish} disabled={posting || (!postText.trim() && !postMedia)} data-testid="onboarding-post-submit" className="rounded-full aura-gradient px-5 py-2 text-sm font-bold text-black disabled:opacity-50">{posting ? "Posting…" : "Post"}</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 5 && (
            <div className="text-center">
              <div className="relative mx-auto w-fit">
                <div className="absolute inset-0 aura-radial blur-2xl opacity-70 scale-150" />
                <Logo size={96} className="relative" />
              </div>
              <h1 className="mt-8 font-display text-4xl font-extrabold aura-text">AURA ACTIVATED.</h1>
              <p className="mt-3 text-am-text2">Welcome to AuraMax, @{username || user?.username}.</p>
              {referrer && <p className="mt-2 text-xs text-am-text3">You're now following @{referrer.username}, who invited you.</p>}
              <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-aura-magenta/30 bg-am-surface2 px-4 py-2"><Sparkles className="h-4 w-4 aura-text" /><span className="font-mark text-sm font-bold aura-text">Fresh Aura unlocked</span></div>
            </div>
          )}
        </div>

        <div className="mt-10 flex items-center gap-3">
          {step > 1 && step < 5 && <button onClick={back} className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-am-border text-white hover:bg-white/5"><ArrowLeft className="h-5 w-5" /></button>}
          {step === 0 ? null : step === 5 ? (
            <button onClick={finish} data-testid="onboarding-enter" className="flex w-full items-center justify-center gap-2 rounded-full bg-white py-4 text-base font-bold text-black transition-transform hover:scale-[1.02]">Enter For You <ArrowRight className="h-5 w-5" /></button>
          ) : step === 4 ? (
            <button onClick={next} data-testid="onboarding-skip-post" className="flex flex-1 items-center justify-center gap-2 rounded-full border border-am-border-strong py-4 text-sm font-semibold text-white hover:bg-white/5">Skip for now</button>
          ) : (
            <button onClick={handleContinue} disabled={!canNext} data-testid="onboarding-continue" className={cn("flex flex-1 items-center justify-center gap-2 rounded-full py-4 text-sm font-bold transition-all", canNext ? "aura-gradient text-black hover:scale-[1.01]" : "cursor-not-allowed bg-am-surface3 text-am-text3")}>{step === 2 && !avatar && !banner && !bio ? "Skip for now" : "Continue"} <ArrowRight className="h-4 w-4" /></button>
          )}
        </div>
      </div>
    </div>
  );
}
